import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { type Scene, VideoData } from '@lumen5/luminary';
import type { GetRootState } from 'js/src/libs/RootState';
import { z } from 'zod';

import type { EnhancedToolResult, ToolExecutionContext } from '../types';

import { resolveSceneHandle } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import { TextPositionRecommendationOptions } from 'js/src/client/types.gen';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { setSlideTextPositionOperation } from 'js/src/luminary/text-editing-operations';

type TextPositionPreset = Exclude<`${TextPositionRecommendationOptions}`, 'auto'>;

// Every preset except 'auto' — the model picks a concrete position, not "auto".
// Kept in enum-declaration order so the emitted enum matches the previous schema.
const TEXT_POSITION_PRESET_VALUES = Object.values(TextPositionRecommendationOptions).filter(
    (value) => value !== TextPositionRecommendationOptions.AUTO,
) as [TextPositionPreset, ...TextPositionPreset[]];

export const setTextPositionPresetSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
    text_position_preset: z
        .enum(TEXT_POSITION_PRESET_VALUES)
        .describe('The text position preset. Not all scenes support all presets.'),
});
export type SetTextPositionPresetArgs = z.infer<typeof setTextPositionPresetSchema>;

export class SetTextPositionPresetTool extends VideoStateTool<typeof setTextPositionPresetSchema> {
    protected override readonly schema = setTextPositionPresetSchema;
    protected override readonly toolName = TOOL_NAMES.setTextPositionPreset;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription =
        'Set text position preset for a scene. Coarser adjustments than manually overriding asset positions. Before calling this tool, read the scene\'s "textPositionPresets" field in the current video state and pass one of those values. Remember! This is a high level tool, so it should always be your first choice when adjusting text positions!';

    public override getToolDisplayName(): string {
        return 'Setting text position preset';
    }

    public get statusIcon(): string {
        return 'format_align_center';
    }

    public async execute(args: SetTextPositionPresetArgs, context: ToolExecutionContext) {
        const { text_position_preset } = args;
        const { parentActionGroup } = context;

        const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
            this.luminary,
            this.getState(),
            args,
        );
        const scene = this.luminary.scenes[scene_index];

        // Set up operations
        const { setSlideTextPosition } = makeOperationsCallable(
            {
                setSlideTextPositionOperation,
            },
            this.luminary,
            this.dispatch,
            this.getState as GetRootState,
        );

        try {
            setSlideTextPosition(scene.id, text_position_preset, false, parentActionGroup);
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : 'Unknown error occurred';
            const validPresets = scene.availableTextPositions;
            const errorPayload = {
                error: errorMessage,
                scene_index,
                requested_text_position_preset: text_position_preset,
                valid_text_position_presets: validPresets,
                ...(validPresets.length === 1 && {
                    hint: `This design has only one text position (${validPresets[0]}), so presets can't move its text. ${describeHowToMoveText(scene)}`,
                }),
            };
            return {
                result: JSON.stringify(errorPayload),
                isSuccess: false,
            } satisfies EnhancedToolResult;
        }
        return `Success: Set text_position_preset to ${text_position_preset} for scene ${sceneShortId}`;
    }
}

/** Which tool moves the scene's text: set_text for free text, set_group_asset for grouped text. */
function describeHowToMoveText(scene: Scene): string {
    const freeTextNames = new Set<string>();
    const groupNames = new Set<string>();
    scene
        .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
        .filter((asset) => asset.isEditable)
        .forEach((asset) => {
            const group = asset.isPositionControlledByParent
                ? asset.getFreelyPositionedParent()
                : null;
            if (group) {
                groupNames.add(group.name);
            } else {
                freeTextNames.add(asset.name);
            }
        });
    const moves = [
        ...(freeTextNames.size > 0
            ? [`Move ${[...freeTextNames].join(', ')} with ${TOOL_NAMES.setText} position.`]
            : []),
        ...(groupNames.size > 0
            ? [
                  `Its text sits in ${[...groupNames].join(', ')}, so move that group with ${TOOL_NAMES.setGroupAsset} position.`,
              ]
            : []),
    ];
    return moves.length > 0
        ? moves.join(' ')
        : `Move the text with ${TOOL_NAMES.setText} position, or its group with ${TOOL_NAMES.setGroupAsset} position.`;
}
